import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormHelperText } from '@mui/material';
import SelectedFileDisplay from './SelectedFileDisplay';
import DropZoneContent from './DropZoneContent';
import styles from './style.module.css';

export default function DragDropFileUpload({
  accept,
  selectedFile,
  onFileChange,
  onFileRemove,
  error,
  helperText,
  maxSize,
  placeholder,
}) {
  const { t } = useTranslation();
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const validateFile = useCallback(
    (file) => {
      const errors = [];

      if (maxSize && file.size > maxSize) {
        errors.push(
          t('errors.fileSizeExceeded', {
            maxSize: (maxSize / 1024 / 1024).toFixed(1),
          })
        );
      }

      if (accept) {
        const acceptedTypes = accept.split(',').map((type) => type.trim());
        const fileExtension = `.${file.name.split('.').pop()}`.toLowerCase();
        const isValidType = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return fileExtension === type.toLowerCase();
          }
          return file.type.match(new RegExp(type.replace('*', '.*')));
        });

        if (!isValidType) {
          errors.push(t('errors.invalidFileType'));
        }
      }

      if (errors.length > 0) {
        onFileChange(null, errors.join('. '));
        return;
      }

      onFileChange(file, null);
    },
    [maxSize, accept, onFileChange, t]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0];
        validateFile(file);
      }
    },
    [validateFile]
  );

  const handleFileInput = useCallback(
    (e) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        validateFile(file);
      }
    },
    [validateFile]
  );

  const handleClick = () => {
    document.getElementById('drag-drop-file-input').click();
  };

  const getContainerClasses = () => {
    let classes = [styles.dragDropContainer];

    if (error) {
      classes.push(styles.error);
    } else if (isDragActive) {
      classes.push(styles.dragActive);
    } else if (selectedFile) {
      classes.push(styles.fileSelected);
    } else {
      classes.push(styles.default);
    }

    return classes.join(' ');
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={getContainerClasses()}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        aria-label={selectedFile ? t('aria.replaceFile') : t('aria.uploadFile')}
        aria-describedby={error ? 'file-upload-error' : 'file-upload-helper'}
      >
        <input
          id="drag-drop-file-input"
          type="file"
          accept={accept}
          className={styles.hiddenFileInput}
          onChange={handleFileInput}
        />

        <DropZoneContent
          selectedFile={selectedFile}
          isDragActive={isDragActive}
          error={error}
          maxSize={maxSize}
          placeholder={placeholder}
        />
      </div>

      <SelectedFileDisplay selectedFile={selectedFile} onFileRemove={onFileRemove} />

      <FormHelperText
        id={error ? 'file-upload-error' : 'file-upload-helper'}
        error={!!error}
        sx={{ mx: 0, mt: 1 }}
      >
        {error || helperText || ' '}
      </FormHelperText>
    </>
  );
}
